import {
  getAdminApiUrl,
  getAccHostname,
  fetchWithToken,
} from '@adsk/acs-sc-web-platform-api';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import {
  getConstructionApiUrl,
  SERVICES,
  SERVICE_STATUSES,
  SERVICE_ACCESSES,
  SERVICES_PRODUCTS_MAP,
} from '@adsk/acs-admin-ui-components-helpers';

const classicSvcNames = ['plan', 'field', 'glue'];

const isClassicSvc = (serviceName) => classicSvcNames.includes(serviceName);

const computeSvcAccessLevel = (memberServices, serviceName, accessLevel) => {
  const { NONE, ADMIN } = SERVICE_ACCESSES;

  // projectAdmin service case
  if (serviceName === SERVICES.PROJECT_ADMIN) {
    return accessLevel === ADMIN ? ADMIN : NONE;
  }

  // classic services case (plan, field, glue)
  if (isClassicSvc(serviceName)) {
    return NONE;
  }

  // other services case
  return memberServices.some(
    (userService) =>
      userService.serviceName === serviceName && userService.access !== NONE
  )
    ? accessLevel
    : NONE;
};

export const getProjectUserServicesAccess = (
  services,
  memberServices,
  accessLevel
) =>
  services.reduce((filtered, service) => {
    const { status, serviceName } = service;

    if (
      status === SERVICE_STATUSES.ACTIVE ||
      status === SERVICE_STATUSES.ACTIVATING
    ) {
      filtered.push({
        serviceName,
        access: computeSvcAccessLevel(memberServices, serviceName, accessLevel),
      });
    }

    return filtered;
  }, []);

export const getProjectUserProductsAccess = (memberProducts, accessLevel) =>
  memberProducts.map((product) => ({
    key: product.key,
    access:
      (product.access === SERVICE_ACCESSES.NONE &&
        product.key !== PRODUCT_IDS.PROJECT_ADMINISTRATION) ||
      (product.key === PRODUCT_IDS.PROJECT_ADMINISTRATION &&
        accessLevel === SERVICE_ACCESSES.MEMBER)
        ? SERVICE_ACCESSES.NONE
        : accessLevel,
  }));

export const buildAccountCompaniesRequest = (accountId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/companies?fields=name&limit=200`;

export const buildGetAccountRolesRequest = (accountId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/roles?limit=200`;

export const buildGetProjectRequest = (projectId, useConstructionApi) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return `${url}/v1/projects/${projectId}`;
};

export const buildUpdateMemberRequest = (
  projectId,
  userId,
  memberData,
  useConstructionApi
) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return [
    `${url}/v1/projects/${projectId}/users/${userId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(memberData),
    },
  ];
};

export const buildRemoveMemberRequest = (
  projectId,
  userId,
  useConstructionApi
) => {
  const url = useConstructionApi ? getConstructionApiUrl() : getAdminApiUrl();

  return [
    `${url}/v1/projects/${projectId}/users/${userId}`,
    {
      method: 'DELETE',
    },
  ];
};

export const buildGetProjectAdminsRequest = (
  projectId,
  useConstructionApis
) => {
  const url = useConstructionApis ? getConstructionApiUrl() : getAdminApiUrl();

  return `${url}/v1/projects/${projectId}/users?limit=5&filter[serviceNames]=projectAdministration`;
};

export const buildAddProjectMemberRequest = (projectId, user) => [
  `${getAdminApiUrl()}/v1/projects/${projectId}/users`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(user),
  },
];

export const getProjectListPageUrl = () => `${getAccHostname()}/projects`;

export const buildGetAccountCompanyUrl = (accountId, companyId) =>
  `${getAdminApiUrl()}/v1/accounts/${accountId}/companies/${companyId}?fields=name`;

export const buildGetProjectEntitlementsUrl = (
  projectId,
  useProjectEntitlementsV2 = false
) => {
  return useProjectEntitlementsV2
    ? `${getAdminApiUrl()}/v2/project-entitlements?currentProjectId=${projectId}&limit=1`
    : `${getAdminApiUrl()}/v1/project-entitlements?currentProjectId=${projectId}&limit=1`;
};

export const doFetch = (url, requestBody, onSuccess, onError) => {
  fetchWithToken(url, requestBody)
    .then((response) => {
      onSuccess(response);
    })
    .catch((error) => {
      onError(error);
    });
};

export const hasAccessToServices = (memberServices) =>
  Object.values(SERVICES)
    .filter((service) => service !== SERVICES.PROJECT_ADMIN)
    .some((service) =>
      memberServices.some(
        (memberService) =>
          service === memberService.serviceName &&
          memberService.access !== SERVICE_ACCESSES.NONE
      )
    );

export const getMemberServicesFromProducts = (products) =>
  products.reduce((result, product) => {
    const maps = SERVICES_PRODUCTS_MAP.filter(
      (map) => map.product === product.key
    );
    maps.forEach((map) => {
      result.push({
        serviceName: map.service,
        access: product.access,
      });
    });

    return result;
  }, []);

export const getMemberProductsFromServices = (memberServices, productIds) =>
  productIds.map((product) => {
    let access = SERVICE_ACCESSES.NONE;
    const services = SERVICES_PRODUCTS_MAP.filter(
      (map) => map.product === product
    );

    if (
      memberServices.some(
        (ms) =>
          services.some((s) => ms.serviceName === s.service) &&
          ms.access === SERVICE_ACCESSES.ADMIN
      )
    ) {
      access = SERVICE_ACCESSES.ADMIN;
    } else if (
      memberServices.some(
        (ms) =>
          services.some((s) => ms.serviceName === s.service) &&
          ms.access === SERVICE_ACCESSES.MEMBER
      )
    ) {
      access = SERVICE_ACCESSES.MEMBER;
    }

    return {
      key: product,
      access,
    };
  });
